import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSystemVehicles,
  createSystemVehicle,
  updateSystemVehicle,
  deleteSystemVehicle,
} from '../../api/maintenance/systemVehicleApi';

// pequeño debounce sin dependencias externas
const useDebounced = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useMemo(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

export const useSystemVehicles = () => {
  const queryClient = useQueryClient();

  // estado de tabla (server-side)
  const [page, setPage] = useState(0);       // 0-based DataGrid
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('A'); // A | I | (vacío para A/I)

  const debouncedSearch = useDebounced(search, 400);

  const queryKey = ['systemVehicles', { page, pageSize, q: debouncedSearch, estreg: status }];

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => fetchSystemVehicles({ page, limit: pageSize, q: debouncedSearch, estreg: status || undefined }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 2,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const createMut = useMutation({
    mutationFn: createSystemVehicle,
    onSuccess: invalidate,
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateSystemVehicle(id, data),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: deleteSystemVehicle,
    // optimista
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData(queryKey);
      if (prev?.items) {
        queryClient.setQueryData(queryKey, {
          ...prev,
          total: prev.total - 1,
          items: prev.items.filter((it) => it.sisvehID !== id && it.sisvehid !== id),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
    },
    onSettled: invalidate,
  });

  return {
    // data normalizada
    items: data?.items || [],
    total: data?.total || 0,
    page,
    pageSize,
    isLoading,
    isError,
    error,

    // setters
    setPage,
    setPageSize,
    search,
    setSearch,
    status,
    setStatus,

    // mutations
    createMut,
    updateMut,
    deleteMut,
  };
};
